import type { DataProxy } from 'apollo-cache';
import cloneDeep from 'lodash/cloneDeep';
import { type FetchResult } from 'apollo-link';

import type { AnnotationInfo } from '@atlaskit/editor-plugins/annotation';

import type { ApolloInteropMutableHelper } from '@confluence/apollo-relay-interop/entry-points/ApolloInteropMutableHelper.ts';
import type { CreateInlineReplyMutationType } from '@confluence/inline-comments-queries/entry-points/CreateInlineReplyMutation.ts';
import type {
	ActiveInlineCommentsQueryType,
	ActiveInlineCommentsQueryVariables,
} from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery.ts';
import type {
	InlineCommentQueryType,
	InlineCommentQueryNode,
	GraphQLContentStatus,
} from '@confluence/inline-comments-queries/entry-points/InlineCommentQuery.ts';
import { CommentCreationLocation } from '@confluence/inline-comments-queries/entry-points/CreateInlineCommentMutation.ts';
import { InlineCommentQuery } from '@confluence/inline-comments-queries/entry-points/InlineCommentQuery.graphql.ts';
import { ActiveInlineCommentsQuery } from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery.graphql.ts';
import { buildConsistentActiveCommentsQueryVariables } from '@confluence/comments-panel-queries/entry-points/buildConsistentQueryVariablesHelper.ts';
import {
	type ActiveCommentsQueryType,
	type ActiveCommentsPanelNode,
} from '@confluence/comments-panel-queries/entry-points/ActiveCommentsQuery.ts';
import { ActiveCommentsQuery } from '@confluence/comments-panel-queries/entry-points/ActiveCommentsQuery.graphql.ts';
import type { ExperienceTrackerAPI } from '@confluence/experience-tracker/entry-points/ExperienceTracker.ts';
import {
	REPLY_TO_INLINE_COMMENT_EXPERIENCE,
	REPLY_TO_INLINE_COMMENT_PUBLISH_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { InlineCommentFramework } from '@confluence/inline-comments-utils/entry-points/enum.ts';
import { getLogger } from '@confluence/logger';
import { END } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import type { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import type { AnalyticsSource } from '@confluence/comments-util/entry-points/analytics.ts';
import { CommentType } from '@confluence/comments-util/entry-points/analytics.ts';
import { getCommentsDataManagerExperimentAttributes } from '@confluence/experience-tracker/entry-points/getCommentsDataManagerExperimentAttributes.ts';

import type { MutationResult } from '../entry-points/inlineCommentsTypes.ts';

import { getTargetNodeType } from './analyticsUtils.ts';

import { readQueryInterop } from '@confluence/apollo-relay-interop/entry-points/readQuery.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';

const logger = getLogger('inline-comments-common');

export type DefaultReactionsSummary = {
	ari: string;
	containerAri: string;
	reactionsCount: number;
	reactionsSummaryForEmoji: any[];
	__typename: 'ReactionsSummaryResponse';
};

const removeReplyFromCache = (
	parentComment: ApolloInteropMutableHelper<ActiveCommentsPanelNode> | InlineCommentQueryNode,
	replyId: string,
) => {
	if (parentComment) {
		// Remove the returned commentId from the cache and reset it
		const idxToRemove = parentComment.replies.findIndex((reply) => reply?.id === replyId);

		if (idxToRemove !== -1) {
			(parentComment.replies as any).splice(idxToRemove, 1);
		}
	}
};

const addReplyToCache = (
	parentComment: ApolloInteropMutableHelper<ActiveCommentsPanelNode> | InlineCommentQueryNode,
	newData: CreateInlineReplyMutationType,
) => {
	if (!parentComment) return;
	const mutationResult = newData.replyInlineComment;
	// Ensure reactionsSummary exists to satisfy queries that select it
	if (!mutationResult?.reactionsSummary) {
		mutationResult.reactionsSummary = defaultReactionsSummary;
	}

	// Temporary workaround since ActiveCommentsQuery was migrated to Relay and InlineCommentQuery is still using Apollo
	// as such, there is a mismatch in types until InlineCommentQuery is also migrated to Relay
	(parentComment.replies as any).push(mutationResult as any);
};

export const updateApolloCacheCallback =
	(
		actionType: 'delete' | 'create',
		queryVariables: {
			pageId: string;
			contentStatus: GraphQLContentStatus[];
			annotationId?: string;
		},
		parentCommentId?: string | null,
		commentId?: string,
	) =>
	(
		_cache: DataProxy,
		result: FetchResult<MutationResult | CreateInlineReplyMutationType>,
	): void => {
		// If the mutation fails, don't do anything
		if (!result || !result.data) {
			return;
		}

		try {
			const commentsPanelVariables = buildConsistentActiveCommentsQueryVariables({
				pageId: queryVariables.pageId,
			});
			const commentsPanelResponse = readQueryInterop<ActiveCommentsQueryType>({
				query: ActiveCommentsQuery,
				variables: commentsPanelVariables,
			}) as ApolloInteropMutableHelper<ActiveCommentsQueryType>;

			if (commentsPanelResponse) {
				const commentsPanelData = cloneDeep(
					commentsPanelResponse,
				) as ApolloInteropMutableHelper<ActiveCommentsQueryType>;

				const commentsPanelParentComment = commentsPanelData?.comments?.nodes?.find(
					(comment: ActiveCommentsPanelNode) => comment?.id === parentCommentId,
				);

				if (commentsPanelParentComment) {
					if (actionType === 'delete') {
						removeReplyFromCache(commentsPanelParentComment, commentId!);
					} else {
						addReplyToCache(
							commentsPanelParentComment,
							result.data as CreateInlineReplyMutationType,
						);
					}
				}

				// Update the comments panel query
				writeQueryInterop({
					query: ActiveCommentsQuery,
					variables: commentsPanelVariables,
					data: commentsPanelData,
				});
			}
		} catch (err) {
			logger.error`An Error occurred when updating comments panel cache for ${actionType} reply - ${err}`;
		}

		try {
			const inlineQueryVariables = { ...queryVariables };
			const inlineCommentResponse = readQueryInterop<InlineCommentQueryType>({
				query: InlineCommentQuery,
				variables: inlineQueryVariables,
			});

			if (inlineCommentResponse) {
				const inlineCommentData = { ...inlineCommentResponse };
				const inlineCommentsParentComment = inlineCommentData?.comments?.nodes?.find(
					(comment) => comment?.id === parentCommentId,
				);

				if (inlineCommentsParentComment) {
					if (actionType === 'delete') {
						removeReplyFromCache(inlineCommentsParentComment, commentId!);
					} else {
						addReplyToCache(
							inlineCommentsParentComment,
							result.data as CreateInlineReplyMutationType,
						);
					}
				}

				// Update the inline comments
				writeQueryInterop({
					query: InlineCommentQuery,
					variables: inlineQueryVariables,
					data: inlineCommentData,
				});
			}
		} catch (err) {
			logger.error`An Error occurred when updating inline comments cache for ${actionType} reply - ${err}`;
		}
	};

export const updateApolloCacheCallbackForGA =
	(
		actionType: 'delete' | 'create',
		queryVariables: {
			pageId: string;
			contentStatus: GraphQLContentStatus[];
			annotationId?: string;
		},
		parentCommentId?: string | null,
		commentId?: string,
	) =>
	(
		_cache: DataProxy,
		result: FetchResult<MutationResult | CreateInlineReplyMutationType>,
	): void => {
		// If the mutation fails, don't do anything
		if (!result || !result.data) {
			return;
		}
		try {
			const standaloneCommentVariables = {
				pageId: queryVariables.pageId,
				contentStatus: queryVariables.contentStatus,
				inlineMarkerRefList: [queryVariables.annotationId],
			};
			const standaloneCommentResponse = readQueryInterop<ActiveInlineCommentsQueryType>({
				query: ActiveInlineCommentsQuery,
				variables: standaloneCommentVariables,
			});

			if (standaloneCommentResponse) {
				const standaloneCommentData = { ...standaloneCommentResponse };
				const standaloneParentComment = standaloneCommentData?.comments?.nodes?.find(
					(comment) => comment?.id === parentCommentId,
				);

				if (standaloneParentComment) {
					if (actionType === 'delete') {
						removeReplyFromCache(standaloneParentComment, commentId!);
					} else {
						addReplyToCache(standaloneParentComment, result.data as CreateInlineReplyMutationType);
					}
				}

				// Update the comments panel query
				writeQueryInterop({
					query: ActiveInlineCommentsQuery,
					variables: standaloneCommentVariables,
					data: standaloneCommentData,
				});
			}
		} catch (err) {
			logger.error`An Error occurred when updating comments panel cache for ${actionType} reply - ${err}`;
		}
	};

export const getCommentCreationLocation = (
	isEditor: boolean,
	isLivePage: boolean,
):
	| CommentCreationLocation.EDITOR
	| CommentCreationLocation.LIVE
	| CommentCreationLocation.RENDERER => {
	if (isLivePage) return CommentCreationLocation.LIVE;
	if (isEditor) return CommentCreationLocation.EDITOR;

	return CommentCreationLocation.RENDERER;
};

interface HandleCreateInlineReplySuccessProps {
	onSuccess: (() => void) | undefined;
	editCommentQueryId: string | undefined;
	setCommentForEdit: any;
	data: any;
	createAnalyticsEvent: any;
	pageId: string | undefined;
	pageType: string;
	analyticsSource: AnalyticsSource;
	commentId: string | undefined;
	pageMode: PageMode;
	selectedAnnotation: AnnotationInfo | undefined;
	getInlineNodeTypes: any;
	experienceTracker: ExperienceTrackerAPI;
	isEditor: boolean;
}

export const handleCreateReplySuccess = ({
	onSuccess,
	editCommentQueryId,
	setCommentForEdit,
	data,
	createAnalyticsEvent,
	pageId,
	pageType,
	analyticsSource,
	commentId,
	pageMode,
	selectedAnnotation,
	getInlineNodeTypes,
	experienceTracker,
	isEditor,
}: HandleCreateInlineReplySuccessProps): void => {
	onSuccess?.();
	if (editCommentQueryId) {
		setCommentForEdit('');
	}

	const replyCommentInfo = data?.replyInlineComment;
	const markerRef = replyCommentInfo?.location?.inlineMarkerRef;

	createAnalyticsEvent({
		type: 'sendTrackEvent',
		data: {
			action: 'created',
			actionSubject: 'comment',
			actionSubjectId: replyCommentInfo?.id, // The newly created comment ID
			objectType: pageType,
			objectId: pageId,
			source: analyticsSource,
			attributes: {
				...getCommentsDataManagerExperimentAttributes(),
				source: analyticsSource,
				commentType: CommentType.INLINE,
				parentCommentId: commentId ?? null, // analytics event schema type expects string or null
				isReply: true,
				pageMode,
				framework: InlineCommentFramework.ANNOTATION_PROVIDER,
				navdexPointType: END,
				inlineNodeTypes:
					selectedAnnotation && getInlineNodeTypes
						? getInlineNodeTypes(selectedAnnotation.id)
						: undefined,
				targetNodeType: getTargetNodeType(markerRef, isEditor),
			},
		},
	}).fire();

	experienceTracker.updateAttributes({
		name: REPLY_TO_INLINE_COMMENT_EXPERIENCE,
		attributes: {
			replyCommentId: replyCommentInfo?.id,
		},
	});

	experienceTracker.succeed({
		name: REPLY_TO_INLINE_COMMENT_PUBLISH_EXPERIENCE,
		attributes: {
			replyCommentId: replyCommentInfo?.id,
		},
	});
};

export const insertReopenedCommentAnnotation = (
	allAnnotations: string[],
	commentMarkerRef: string,
	previousOrderedActiveAnnotations: string[],
	setOrderedActiveAnnotationIdList: (annotations: string[]) => void,
): void => {
	const indexInAnnotations = allAnnotations.indexOf(commentMarkerRef);

	if (indexInAnnotations === -1) {
		throw new Error('Marker ref is not present in the annotations list');
	}

	const insertIndex = previousOrderedActiveAnnotations.findIndex(
		(annotation) => allAnnotations.indexOf(annotation) > indexInAnnotations,
	);

	const finalInsertIndex =
		insertIndex === -1 ? previousOrderedActiveAnnotations.length : insertIndex;

	const updatedOrderedActiveAnnotations = [
		...previousOrderedActiveAnnotations.slice(0, finalInsertIndex),
		commentMarkerRef,
		...previousOrderedActiveAnnotations.slice(finalInsertIndex),
	];
	setOrderedActiveAnnotationIdList(updatedOrderedActiveAnnotations);
};

export const inlineCommentExists = (inlineMarkerRef: string | null): boolean => {
	const tiny = `.inline-comment-marker[data-ref="${inlineMarkerRef}"]`;
	const fabricEditorAnnotation = `[id="${inlineMarkerRef}"][data-mark-annotation-type="inlineComment"]`;

	return Boolean(document.querySelector(`${tiny}, ${fabricEditorAnnotation}`));
};

export const defaultReactionsSummary: DefaultReactionsSummary = {
	ari: '',
	containerAri: '',
	reactionsCount: 0,
	reactionsSummaryForEmoji: [],
	__typename: 'ReactionsSummaryResponse',
};

export const updateReactionsCacheCallbackForGA = (
	emojiId: string,
	actionType: 'add' | 'delete',
	objectId: string,
	pageId: string,
	selectedAnnotation: AnnotationInfo,
	isArchived: boolean,
): void => {
	try {
		const queryVariables = {
			pageId,
			inlineMarkerRefList: [selectedAnnotation.id],
			contentStatus: [isArchived ? 'ARCHIVED' : 'DRAFT', 'CURRENT'] as GraphQLContentStatus[],
		};

		const response: ActiveInlineCommentsQueryType | null | undefined = readQueryInterop<
			ActiveInlineCommentsQueryType,
			ActiveInlineCommentsQueryVariables
		>({
			query: ActiveInlineCommentsQuery,
			variables: queryVariables,
		});

		if (!response?.comments?.nodes?.[0]) {
			return;
		}

		const comment = response.comments.nodes[0];
		let cacheReactionsSummary;
		let reply;

		// If the comment matches the comment ID, use it's reactionsSummary
		if (comment.id === objectId) {
			cacheReactionsSummary = comment.reactionsSummary
				? { ...comment.reactionsSummary }
				: defaultReactionsSummary;
		} else {
			// Otherwise, find the reply that matches
			reply = comment.replies.find((r) => r?.id === objectId);
			if (reply) {
				cacheReactionsSummary = reply.reactionsSummary
					? { ...reply.reactionsSummary }
					: defaultReactionsSummary;
			}
		}

		if (!cacheReactionsSummary) {
			return;
		}

		const reactionsAri = cacheReactionsSummary.ari || '';
		const reactionsContainerAri = cacheReactionsSummary.containerAri || '';
		let reactionsCount = cacheReactionsSummary.reactionsCount || 0;
		const updatedReactionsSummary = { ...cacheReactionsSummary };
		const updatedReactionsNodes = [...(cacheReactionsSummary.reactionsSummaryForEmoji || [])];

		if (actionType === 'add') {
			const reaction = updatedReactionsNodes.find((item) => item?.emojiId === emojiId);
			// If reaction exists, update its attributes
			if (reaction && !reaction.reacted) {
				reaction.count++;
				reaction.reacted = true;
			} else {
				// Otherwise, create a new reaction
				const newReaction = {
					emojiId,
					count: 1,
					reacted: true,
					id: `${reactionsContainerAri}|${reactionsAri}|${emojiId}`,
					__typename: 'ReactionsSummaryForEmoji' as const,
				};
				(updatedReactionsNodes as any).push(newReaction);
			}
			++reactionsCount;
		} else if (actionType === 'delete') {
			const reactionIdx = updatedReactionsNodes.findIndex((item) => item?.emojiId === emojiId);
			// If reaction exists, update its attributes
			if (reactionIdx !== -1) {
				const reaction = updatedReactionsNodes[reactionIdx];
				// If we have more than one reaction, remove our user from it and decrement the count
				if (reaction && reaction.count > 1) {
					reaction.count--;
					reaction.reacted = false;
				} else {
					// Otherwise, remove the reaction entirely
					(updatedReactionsNodes as any).splice(reactionIdx, 1);
				}
				--reactionsCount;
			}
		}

		updatedReactionsSummary.reactionsSummaryForEmoji = updatedReactionsNodes;
		updatedReactionsSummary.reactionsCount = reactionsCount ?? 0;

		// Create a deep copy of the comment to ensure Apollo detects the change
		const updatedComment = {
			...comment,
			replies: comment.replies.map((r) => {
				if (r?.id === objectId) {
					return {
						...r,
						reactionsSummary: updatedReactionsSummary,
					};
				}
				return r;
			}),
		};

		// Update the comment's reactionsSummary if it's the target comment
		if (comment.id === objectId) {
			updatedComment.reactionsSummary = updatedReactionsSummary;
		}

		const newData = {
			comments: {
				nodes: [updatedComment],
				__typename: 'PaginatedCommentList',
			},
		};

		writeQueryInterop({
			query: ActiveInlineCommentsQuery,
			variables: queryVariables,
			data: newData,
		});
	} catch (err) {
		logger.error`An error occurred when updating reactions cache for ${actionType} - ${err}`;
	}
};
